import { FC, MutableRefObject, useEffect } from 'react'
import { useFormikContext } from 'formik'
import { Configuration, Group } from '@/api'
import DialogUploadValues from '@/types/dialogUploadValues'
import {
  getDefaultWizardGroupId,
  getIntelligentWizardGroupId,
  getWizardConfigurations,
} from './utils/wizardGroupConfigurations'

export interface WizardDefaultsProps {
  myGroups?: Group[]
  configurations?: Configuration[]
  groupTouchedRef: MutableRefObject<boolean>
}

/**
 * Pré-remplit l'agence dès que les groupes sont connus, y compris à l'étape
 * Fichiers. En préparation intelligente, le groupe `selectAsDefault` (agence
 * la plus utilisée) l'emporte. En classique, l'organisation racine.
 * Le type de déclaration n'est pré-rempli qu'en préparation classique.
 * Un choix manuel d'agence n'est pas écrasé.
 */
export function useWizardDefaults(
  myGroups: Group[] | undefined,
  configurations: Configuration[] | undefined,
  groupTouchedRef: MutableRefObject<boolean>
) {
  const { values, setFieldValue } = useFormikContext<DialogUploadValues>()

  useEffect(() => {
    if (!myGroups?.length || groupTouchedRef.current) return
    const defaultGroupId = values.agentic
      ? getIntelligentWizardGroupId(myGroups)
      : getDefaultWizardGroupId(myGroups)
    if (defaultGroupId && values.group !== defaultGroupId) {
      setFieldValue('group', defaultGroupId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myGroups, values.agentic])

  const groupId = values.group

  useEffect(() => {
    if (values.agentic) return
    const groupConfigurations = getWizardConfigurations(
      configurations,
      groupId,
      myGroups
    )
    const nextCid = groupConfigurations[0]?.cid ?? ''
    const currentIsValid = groupConfigurations.some(
      (c) => c.cid === values.exportConfiguration
    )
    if (!currentIsValid && values.exportConfiguration !== nextCid) {
      setFieldValue('exportConfiguration', nextCid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurations, groupId, values.agentic])
}

/** Pont Formik : le hook ne peut pas être appelé dans le render-prop. */
export const WizardDefaults: FC<WizardDefaultsProps> = ({
  myGroups,
  configurations,
  groupTouchedRef,
}) => {
  useWizardDefaults(myGroups, configurations, groupTouchedRef)
  return null
}

export default WizardDefaults
